/**
 * The one toast that sums up a re-send — success, partial, or nothing went.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { useToast } from '@/providers/ToastProvider';
import { formatDateTimeDMYHM } from '@/features/smswiser-campaigns/utils/dateFormat';

import type { ResendKilledResult } from '@/features/resend-killed-drops/resendKilledApi';

type Toaster = ReturnType<typeof useToast>;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function sentLine(result: ResendKilledResult): string {
  const starts = result.results.flatMap((r) => (r.status === 'sent' && r.executeAfter ? [r.executeAfter] : []));
  if (starts.length === 0) return `${plural(result.sent, 'new drop')} going out now.`;
  const first = formatDateTimeDMYHM(starts.sort()[0]);
  if (result.sent === 1) return `1 new drop scheduled for ${first}.`;
  return `${plural(result.sent, 'new drop')} scheduled — the first at ${first}.`;
}

export function resendToast(toast: Toaster, result: ResendKilledResult): void {
  const firstError = result.results.find((r) => r.status === 'error');
  const ambiguous = result.results.some((r) => r.status === 'error' && r.ambiguous);
  if (result.failed === 0 && result.sent > 0) {
    toast.success(`Re-sent ${plural(result.sent, 'killed drop')}`, sentLine(result));
    return;
  }
  if (result.sent === 0 && result.failed === 0) {
    toast.warning('Nothing re-sent', 'The selected drops were already re-sent or are no longer killed.');
    return;
  }
  const detail = firstError?.status === 'error' ? firstError.message : '';
  const lead = `${result.sent} of ${result.sent + result.failed} re-sent.`;
  (ambiguous ? toast.warning : toast.error)('Some drops were not re-sent', `${lead} ${detail}`.trim());
}
